import React, { useEffect, useState } from "react";
import { Timestamp } from "firebase/firestore";
import { Edit3, Loader2, Plus, Trash2 } from "lucide-react";
import { BLOCK_TYPES } from "../config";
import {
  DAY_OPTIONS,
  getLondonTodayISO,
  isValidTimeRange,
  londonLocalToUtcIso,
} from "../bookingUtils";
import {
  deletePhoenixBlock,
  savePhoenixBlock,
  subscribePhoenixBlocks,
  subscribePhoenixSpaces,
} from "../service";
import {
  AdminPageHeader,
  displaySpaceNames,
  formatTimestamp,
  PhoenixAdminNav,
} from "./AdminShared";

const emptyBlock = {
  id: "",
  spaceIds: [],
  date: "",
  startTime: "",
  endTime: "",
  reason: "",
  blockType: "Maintenance",
  recurrenceType: "none",
  recurrenceEndDate: "",
  recurrenceDays: [],
};

export default function PhoenixBlockedPeriods() {
  const [blocks, setBlocks] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const today = getLondonTodayISO();

  useEffect(() => {
    const unsubscribers = [
      subscribePhoenixBlocks(setBlocks, () => setError("Blocked periods could not be loaded.")),
      subscribePhoenixSpaces(setSpaces, () => {}),
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, []);

  const update = (key, value) => setEditing((current) => ({ ...current, [key]: value }));
  const toggleSpace = (spaceId) => setEditing((current) => {
    if (spaceId === "whole-centre") return { ...current, spaceIds: current.spaceIds.includes(spaceId) ? [] : [spaceId] };
    const withoutWhole = current.spaceIds.filter((id) => id !== "whole-centre");
    return { ...current, spaceIds: withoutWhole.includes(spaceId) ? withoutWhole.filter((id) => id !== spaceId) : [...withoutWhole, spaceId] };
  });
  const toggleDay = (day) => setEditing((current) => ({ ...current, recurrenceDays: current.recurrenceDays.includes(day) ? current.recurrenceDays.filter((item) => item !== day) : [...current.recurrenceDays, day] }));

  const save = async () => {
    setError(""); setMessage("");
    if (!editing.spaceIds.length) { setError("Choose at least one space."); return; }
    if (!editing.date || editing.date < today) { setError("Choose today or a future date."); return; }
    if (!isValidTimeRange(editing.startTime, editing.endTime)) { setError("End time must be later than start time."); return; }
    if (!editing.reason.trim()) { setError("Add a reason for the block."); return; }
    if (editing.recurrenceType !== "none" && (!editing.recurrenceEndDate || editing.recurrenceEndDate < editing.date)) { setError("Add a valid recurrence end date."); return; }
    setSaving(true);
    try {
      await savePhoenixBlock({
        ...editing,
        startDateTime: Timestamp.fromDate(new Date(londonLocalToUtcIso(editing.date, editing.startTime))),
        endDateTime: Timestamp.fromDate(new Date(londonLocalToUtcIso(editing.date, editing.endTime))),
        recurrence: editing.recurrenceType === "none" ? null : {
          frequency: editing.recurrenceType,
          endDate: editing.recurrenceEndDate,
          daysOfWeek: editing.recurrenceDays,
        },
      });
      setEditing(null); setMessage("Blocked period saved.");
    } catch (saveError) { setError(saveError?.message || "The blocked period could not be saved."); }
    finally { setSaving(false); }
  };

  const remove = async (block) => {
    if (!window.confirm(`Remove the ${block.blockType} block on ${block.date}?`)) return;
    try { await deletePhoenixBlock(block.id); setMessage("Blocked period removed."); }
    catch (deleteError) { setError(deleteError?.message || "The blocked period could not be removed."); }
  };

  return (
    <div className="phx-shell phx-admin-shell">
      <PhoenixAdminNav />
      <AdminPageHeader title="Blocked dates and times"><p>Close one space, several spaces or the whole centre for maintenance, UUN programmes, cleaning and other unavailable periods.</p></AdminPageHeader>
      {error ? <div className="phx-submit-error" role="alert">{error}</div> : null}{message ? <div className="phx-availability-available" role="status">{message}</div> : null}
      <div className="phx-admin-actions"><button type="button" className="phx-button" onClick={() => setEditing({ ...emptyBlock })}><Plus aria-hidden="true" /> Add blocked period</button></div>

      {editing ? <section className="phx-admin-card"><h2>{editing.id ? "Edit blocked period" : "Add blocked period"}</h2><div className="phx-admin-form"><fieldset className="phx-full"><legend>Spaces *</legend><div className="phx-checkbox-grid">{spaces.map((space) => <label key={space.id}><input type="checkbox" checked={editing.spaceIds.includes(space.id)} onChange={() => toggleSpace(space.id)} /> {space.name}</label>)}</div></fieldset><label>Date *<input type="date" min={today} value={editing.date} onChange={(e) => update("date", e.target.value)} /></label><label>Block type *<select value={editing.blockType} onChange={(e) => update("blockType", e.target.value)}>{BLOCK_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label><label>Start time *<input type="time" value={editing.startTime} onChange={(e) => update("startTime", e.target.value)} /></label><label>End time *<input type="time" value={editing.endTime} onChange={(e) => update("endTime", e.target.value)} /></label><label className="phx-full">Internal reason *<textarea rows="3" value={editing.reason} onChange={(e) => update("reason", e.target.value)} maxLength="1000" /></label><label>Recurrence<select value={editing.recurrenceType} onChange={(e) => update("recurrenceType", e.target.value)}><option value="none">One-off</option><option value="weekly">Weekly</option><option value="fortnightly">Fortnightly</option><option value="monthly">Monthly</option></select></label>{editing.recurrenceType !== "none" ? <><label>Recurrence end date<input type="date" min={editing.date || today} value={editing.recurrenceEndDate} onChange={(e) => update("recurrenceEndDate", e.target.value)} /></label><fieldset className="phx-full"><legend>Recurring days</legend><div className="phx-checkbox-grid">{DAY_OPTIONS.map(([key, label]) => <label key={key}><input type="checkbox" checked={editing.recurrenceDays.includes(key)} onChange={() => toggleDay(key)} /> {label}</label>)}</div></fieldset></> : null}</div><div className="phx-admin-actions"><button type="button" className="phx-button" onClick={save} disabled={saving}>{saving ? <Loader2 className="phx-spin" aria-hidden="true" /> : null} Save blocked period</button><button type="button" className="phx-button phx-button-secondary" onClick={() => setEditing(null)}>Cancel</button></div></section> : null}

      <section className="phx-admin-card"><h2>Current and future blocks</h2>{blocks.length ? blocks.map((block) => <article className="phx-block-row" key={block.id}><div><p className="phx-kicker">{block.blockType}</p><strong>{block.date || formatTimestamp(block.startDateTime, { dateOnly: true })} · {block.startTime || formatTimestamp(block.startDateTime)}–{block.endTime || formatTimestamp(block.endDateTime)}</strong><p>{displaySpaceNames(block.spaceIds, spaces)}</p><p>{block.reason}</p>{block.recurrence ? <small>Repeats {block.recurrence.frequency} until {block.recurrence.endDate}</small> : null}</div><div className="phx-admin-actions"><button type="button" className="phx-button phx-button-secondary" onClick={() => setEditing({ ...emptyBlock, ...block, recurrenceType: block.recurrence?.frequency || "none", recurrenceEndDate: block.recurrence?.endDate || "", recurrenceDays: block.recurrence?.daysOfWeek || [] })}><Edit3 aria-hidden="true" /> Edit</button><button type="button" className="phx-button phx-danger-button" onClick={() => remove(block)}><Trash2 aria-hidden="true" /> Remove</button></div></article>) : <div className="phx-admin-empty"><h3>No blocked periods</h3><p>Add maintenance, closure or programme time to protect availability.</p></div>}</section>
    </div>
  );
}

